import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  CreditCard,
  Building2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  FileText,
  DollarSign,
  Calendar,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PublicPayment = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const cancelled = searchParams.get("cancelled");
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (invoiceId && token) {
      fetchInvoice();
    } else {
      setError("Invalid payment link");
      setLoading(false);
    }
    
    if (cancelled) {
      toast.error("Payment was cancelled");
    }
  }, [invoiceId, token]);

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API}/api/pay/${invoiceId}?token=${token}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to load invoice");
      }
      const data = await response.json();
      setInvoice(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await fetch(
        `${API}/api/pay/${invoiceId}/checkout?token=${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to create payment session");
      }
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rnb-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
              Payment Link Error
            </h1>
            <p className="text-rnb-gray mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (invoice?.status === "paid") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
              Invoice Already Paid
            </h1>
            <p className="text-rnb-gray mb-6">
              This invoice has already been paid. Thank you!
            </p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center text-rnb-blue hover:text-rnb-navy">
            <ArrowLeft className="w-4 h-4 mr-2" />
            RNB Consulting Group
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Invoice Details */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-rnb-blue" />
                </div>
                <div>
                  <h1 className="font-heading text-xl font-bold text-rnb-navy">
                    Invoice Payment
                  </h1>
                  <p className="text-sm text-rnb-gray">
                    Invoice #{invoiceId}
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-3 border-b">
                  <span className="text-rnb-gray">Client</span>
                  <span className="font-medium text-rnb-navy">{invoice?.client_name}</span>
                </div>
                <div className="flex justify-between py-3 border-b">
                  <span className="text-rnb-gray">Description</span>
                  <span className="font-medium text-rnb-navy text-right max-w-[200px]">
                    {invoice?.description}
                  </span>
                </div>
                {invoice?.due_date && (
                  <div className="flex justify-between py-3 border-b">
                    <span className="text-rnb-gray flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Due Date
                    </span>
                    <span className="font-medium text-rnb-navy">
                      {new Date(invoice.due_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Line Items */}
              {invoice?.line_items?.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-rnb-navy mb-3">Line Items</p>
                  <div className="space-y-2">
                    {invoice.line_items.map((item, idx) => (
                      <div key={item.title || item.name || item.label || idx} className="flex justify-between text-sm py-2 border-b border-dashed">
                        <span className="text-rnb-gray">{item.description}</span>
                        <span className="text-rnb-navy">${item.amount?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="bg-rnb-light rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-rnb-navy">Total Due</span>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-6 h-6 text-rnb-blue" />
                    <span className="font-mono-numbers text-3xl font-bold text-rnb-navy">
                      {invoice?.amount?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Options */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <h2 className="font-heading text-xl font-bold text-rnb-navy mb-6">
                Complete Payment
              </h2>

              <p className="text-rnb-gray mb-6">
                Click the button below to securely pay this invoice. You'll be able to choose from 
                multiple payment options including credit/debit cards, ACH bank transfer, and more.
              </p>

              <Button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="w-full py-6 text-lg bg-rnb-teal hover:bg-rnb-teal/90"
                data-testid="pay-now-btn"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ${invoice?.amount?.toLocaleString()} Now
                  </>
                )}
              </Button>

              {/* Accepted Payment Methods */}
              <div className="mt-6 flex items-center justify-center gap-4 text-rnb-gray">
                <CreditCard className="w-5 h-5" />
                <Building2 className="w-5 h-5" />
                <span className="text-sm">Cards, ACH & more accepted</span>
              </div>

              {/* Security Note */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-rnb-gray text-center">
                  🔒 Your payment is processed securely by Stripe. 
                  RNB Consulting Group never stores your payment details.
                </p>
              </div>

              {/* Help */}
              <div className="mt-6 text-center">
                <p className="text-sm text-rnb-gray">
                  Questions? Contact us at{" "}
                  <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-blue hover:underline">
                    info@rnbconsultinggroup.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PublicPayment;
